import React, { useEffect, useMemo } from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';
import { WidgetViewMode } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { useProjectTreeStore } from 'store/projectContract.store';

import { getActiveFilterCount } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import NoData from 'components/common/NoData/noData';

import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { useUpdateIfReady } from 'components/Dashboards/Reporting/widgetInReport/hooks/RefreshWidgetPayload/useUpdateIfReady';
import { useKeyActivitiesProvider } from 'components/widgets/KeyActivities/KeyActivities.provider';
import { getNextDateCalculation } from 'components/widgets/KeyActivities/components/filter.utils';

export const useKeyActivitiesLogic = ({
    globalFilterData,
    api,
    ui,
    table,
    setIsPayloadReady,
    widgetViewMode,
}: {
    globalFilterData: any;
    api: ReturnType<typeof useKeyActivitiesProvider>['api'];
    ui: ReturnType<typeof useKeyActivitiesProvider>['ui'];
    table: ReturnType<typeof useBaseTable>['table'];
    setIsPayloadReady?: (newValue: boolean) => void;
    widgetViewMode?: WidgetViewMode;
}) => {
    const isPayloadReady = () => {
        const currentPayload = api.getKeyActivities.payload;
        if (!currentPayload) return;
        if (
            currentPayload.compareVersionId === undefined ||
            currentPayload.latestVersionId === undefined ||
            currentPayload.contractId === undefined ||
            currentPayload.contractId === null
        ) {
            return;
        }
        setIsPayloadReady && setIsPayloadReady(true);
    };

    const { project, contract } = useProjectTreeStore();
    const defaultValues = useGlobalFilterDataStore((store) => store.defaultValues);
    useUpdateIfReady({ isPayloadReadyCallback: isPayloadReady, widgetViewMode: widgetViewMode });
    const activeFilterCount = getActiveFilterCount(globalFilterData, Object.keys(defaultValues));
    const isGlobalFilterEmpty = activeFilterCount === 0;

    const noDataText = !isGlobalFilterEmpty ? (
        <NoData>Not applicable with the applied filter</NoData>
    ) : api.getKeyActivitiesTags.instance.data?.length === 0 ? (
        <NoData>
            Select existing tag from list, or open{' '}
            <Link to={AppRoutes.buildPath(AppRoutes.DASHBOARD_EXECUTION_PATH, executionConfig.battlecards.link)}>
                {executionConfig.battlecards.title}
            </Link>{' '}
            to define a tag which identifies the key activities.
        </NoData>
    ) : undefined;

    const activeData = useMemo(() => {
        if (api.getKeyActivities.instance.data) {
            const current = api.getKeyActivities.instance.data.periodItemsList.find((item) => item.id === 'prev');
            return current ? current.itemsList : [];
        }
        return [];
    }, [api.getKeyActivities.instance.data]);

    const filteredData = useMemo(() => {
        const { name, status, moreFilter, nextDate } = ui.state;
        const moreFiltersArray = moreFilter?.map((item) => item.value) ?? [];
        const statusArray = status?.map((item) => item.value);
        if (activeData && isGlobalFilterEmpty) {
            return activeData
                .filter((item) => statusArray?.includes(item.latestVersion.cardBoardViewResponse.status.name))
                .filter((item) =>
                    item.latestVersion.cardBoardViewResponse.title
                        .toLowerCase()
                        .includes(name ? name.toLowerCase() : ''),
                )
                .filter((item) =>
                    moreFiltersArray.includes('LateFinishDate') ? item.latestVersion.finishVariance > 0 : true,
                )
                .filter((item) =>
                    moreFiltersArray.includes('LateStartDate') ? item.latestVersion.startVariance > 0 : true,
                )
                .filter((item) =>
                    moreFiltersArray.includes('CriticalPath')
                        ? item.latestVersion.cardBoardViewResponse.cardIndicators.isCriticalPath.isIndicatorOn
                        : true,
                )
                .filter((item) =>
                    moreFiltersArray.includes('Milestones')
                        ? item.latestVersion.cardBoardViewResponse.taskActivityType === 'Milestone'
                        : true,
                )
                .filter((item) =>
                    nextDate?.value !== '-1'
                        ? getNextDateCalculation(
                              nextDate?.value,
                              item.latestVersion.startDate,
                              item.latestVersion.finishDate,
                          )
                        : true,
                );
        }

        return [];
    }, [activeData, ui.state, isGlobalFilterEmpty]);

    const counter = filteredData.length ? `(${filteredData.length})` : undefined;

    useEffect(() => {
        table.setColumnFilters([
            { id: 'status', value: ui.state.status },
            { id: 'tags', value: ui.state.tags },
            { id: 'activity', value: ui.state.name },
            { id: 'moreFilter', value: ui.state.moreFilter },
            { id: 'nextDate', value: ui.state.nextDate },
        ]);
    }, [ui.state.status, ui.state.tags, ui.state.name, ui.state.moreFilter, ui.state.nextDate]);

    return {
        logic: {
            noDataText,
            isGlobalFilterEmpty,
            activeData,
            filteredData,
            counter,
            exportCsv: {
                headers: getHeaders({ table }),
                rows: getRows({ table }),
                fileName: `key_activities_${project?.title}_${contract?.title}_${moment().format(constants.formats.date.yearMonthDay)}`,
            },
        },
    };
};

const getHeaders = ({ table }) => {
    const originalHeaders = table.tableRef.current
        ? table.tableRef.current.table.getVisibleLeafColumns().map((column) => {
              const header =
                  typeof column.columnDef.header === 'string'
                      ? column.columnDef.header
                      : (column.columnDef.meta?.columnLabel ?? column.id);
              return {
                  key: column.id,
                  name: header,
              };
          })
        : [];
    return [{ key: 'activityId', name: 'Activity ID' }, ...originalHeaders];
};

const getRows = ({ table }) => {
    return table.tableRef.current
        ? table.tableRef.current.table.getFilteredRowModel().rows.map((row) => {
              const originalValues = table.tableRef.current.table.getVisibleLeafColumns().map((col) => {
                  const value = row.getValue(col.id);

                  const format = col.columnDef.meta?.csvFormat as ((val: unknown, row: unknown) => unknown) | undefined;
                  return escapeCSV(format ? format(value, row) : value);
              });

              return [row.original.latestVersion.activityId, ...originalValues];
          })
        : [];
};

const escapeCSV = (value: unknown): string => {
    if (value == null) return '';
    const s = String(value).replace(/"/g, '""');
    return /[",\n\r]/.test(s) ? `"${s}"` : s;
};
